import { faLocationCrosshairs } from '@awesome.me/kit-a1e2571845/icons/classic/solid'
import { Icon as XweIcon } from '@xweather/theme'
import { debounce } from 'es-toolkit/compat'
import { LngLat } from 'mapbox-gl'
import { type ComponentProps, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Box } from '../components/Box.tsx'
import { Divider, Paper, Typography } from '../components/generic.tsx'
import { Icon } from '../components/Icon.tsx'
import { Stack } from '../components/Stack.tsx'
import { type UserPreferences, usePreferences } from '../data/userProfile.ts'
import { ids } from '../ids.ts'
import { useMap } from '../states.ts'
import { useMapCursorLngLat } from './map/mapUtils.ts'

export const CoordsAndScale = (props: ComponentProps<typeof Stack>) => {
	const maxWidth = 104
	return (
		<Stack className='no-zoom' spacing={1 / 4} {...props}>
			<Paper variant='outlined' sx={{ bgcolor: (t) => t.palette.background.paper2, h: 24, w: maxWidth + 16 }}>
				<ScaleDisplay maxWidth={maxWidth} />
			</Paper>
			<Paper variant='outlined' sx={{ bgcolor: (t) => t.palette.background.paper2, h: 24 }}>
				<Coords />
			</Paper>
		</Stack>
	)
}

const Coords = () => {
	const [mouseLocation, setMouseLocation] = useState<{ lat: number; lng: number } | undefined>(undefined)
	const onLngLatChange = useMemo(
		() =>
			debounce((lngLat: [number, number] | undefined) => setMouseLocation(lngLat && new LngLat(lngLat[0], lngLat[1]).wrap()), 25, {
				maxWait: 85,
				trailing: true,
			}),
		[],
	)

	useMapCursorLngLat(onLngLatChange)
	useEffect(() => () => onLngLatChange.cancel(), [onLngLatChange])

	const { lat, lng } = mouseLocation ?? {}
	return (
		<Stack full center sx={{ px: 1 }}>
			<XweIcon icon={faLocationCrosshairs} />
			<Typography data-ta={ids.map.locationAndScaleBox.location} variant='code2' whiteSpace='preserve'>
				{lat !== undefined && lng !== undefined ? `${lat.toFixed(3).padStart(8)}, ${lng.toFixed(3).padStart(8)}` : '--------, --------'}
			</Typography>
		</Stack>
	)
}

export const CoordsAndScaleOld = () => {
	const [mouseLocation, setMouseLocation] = useState<{ latitude: number; longitude: number } | undefined>(undefined)
	const lat = mouseLocation?.latitude
	const lon = mouseLocation?.longitude
	const onLngLatChange = useMemo(
		() =>
			debounce(
				(lngLat: [number, number] | undefined) => {
					if (lngLat) setMouseLocation({ longitude: lngLat[0], latitude: lngLat[1] })
					else setMouseLocation(undefined)
				},
				25,
				{ maxWait: 85, trailing: true },
			),
		[],
	)

	useMapCursorLngLat(onLngLatChange)
	useEffect(() => () => onLngLatChange.cancel(), [onLngLatChange])

	return (
		<Stack col center spacing={0.5} divider={<Divider flexItem />} height={48}>
			<Stack data-ta={ids.map.locationAndScaleBox.location} center gap={0.5} minWidth={180} sx={{ px: 1 }}>
				<Icon name='LocationCrosshairs' size='small' sx={{ color: (t) => t.palette.grey[600] }} />
				<Typography variant='code2' whiteSpace='nowrap' sx={{ lineHeight: 1 }}>
					{lat !== undefined && lon !== undefined ? (
						<>
							{lat < 10 && lat > -10 && space}
							{lat > 0 && '+'}
							{lat.toFixed(3)},{lon < 100 && lon > -100 && space}
							{lon < 10 && lon > -10 && space}
							{lon > 0 && '+'}
							{lon.toFixed(3)}
						</>
					) : (
						'------, ------'
					)}
				</Typography>
			</Stack>
			<ScaleDisplayOld />
		</Stack>
	)
}

export const MapCoordsAndScale = () => (
	<Paper sx={{ position: 'absolute', top: 0, right: 0, m: 1 }}>
		<CoordsAndScaleOld />
	</Paper>
)

const space = '\u00A0'

// https://wiki.openstreetmap.org/wiki/Zoom_levels as source for the 78.272 km/pixel
const distancePerPixelValues: Record<UserPreferences['unitsDistance'], number> = {
	KILOMETRES: 78.272,
	MILES: 48.636,
	NAUTICAL_MILES: 42.263,
}

const ScaleDisplay = ({ maxWidth }: { maxWidth: number }) => {
	const scaleBreakpoints = [
		0.0001, 0.0002, 0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000,
	]
	const getScaleWidth = (pixelsPerDistance: number) => {
		const scale = scaleBreakpoints.find((bp) => pixelsPerDistance / bp < maxWidth)
		if (!scale) return { distance: 0, pixels: maxWidth }
		return { distance: 1 / scale, pixels: pixelsPerDistance / scale }
	}

	const map = useMap((s) => s.map)
	const { data: pref } = usePreferences()
	const [pixelsPerDistance, setPixelsPerDistance] = useState(0)

	useLayoutEffect(() => {
		if (!map) return

		const distancePerPixel = distancePerPixelValues[pref?.unitsDistance || 'KILOMETRES']
		const updateScale = () => {
			setPixelsPerDistance(2 ** map.getZoom() / Math.cos((Math.abs(map.getCenter().lat) * Math.PI) / 180) / distancePerPixel)
		}
		updateScale()
		map?.on('move', updateScale)
		return () => {
			map?.off('move', updateScale)
		}
	}, [map, pref?.unitsDistance])

	const { distance, pixels } = getScaleWidth(pixelsPerDistance)

	return (
		<Stack data-ta={ids.map.locationAndScaleBox.scale} full col center spacing={0}>
			{pref?.unitsDistance === 'KILOMETRES' /*|| pref?.unitsDistance === 'METRES'*/ && (
				<Typography variant='code2' sx={{ lineHeight: 1 }}>
					{distance < 1 ? `${distance * 1000} m` : `${distance} km`}
				</Typography>
			)}
			{pref?.unitsDistance === 'MILES' && (
				<Typography variant='code2' sx={{ lineHeight: 1 }}>
					{distance < 1 ? `${distance * 1760} yd` : `${distance} mi`}
				</Typography>
			)}
			{pref?.unitsDistance === 'NAUTICAL_MILES' && <Typography variant='code2' sx={{ lineHeight: 1 }}>{`${distance} nmi`}</Typography>}
			<Divider sx={{ borderColor: ({ palette }) => palette.text.primary, borderWidth: 1 }} style={{ width: `${pixels}px` }} />
		</Stack>
	)
}

const ScaleDisplayOld = () => {
	const scaleMaxWidth = 150
	const scaleBreakpoints = [
		0.0001, 0.0002, 0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000,
	]
	const getScaleWidth = (pixelsPerDistance: number) => {
		const scale = scaleBreakpoints.find((bp) => pixelsPerDistance / bp < scaleMaxWidth)
		if (!scale) return { distance: 0, pixels: scaleMaxWidth }
		return { distance: 1 / scale, pixels: pixelsPerDistance / scale }
	}

	const map = useMap((s) => s.map)
	const { data: pref } = usePreferences()
	const [pixelsPerDistance, setPixelsPerDistance] = useState(0)

	useLayoutEffect(() => {
		if (!map) return

		const distancePerPixel = distancePerPixelValues[pref?.unitsDistance || 'KILOMETRES']
		const updateScale = () => {
			setPixelsPerDistance(2 ** map.getZoom() / Math.cos((Math.abs(map.getCenter().lat) * Math.PI) / 180) / distancePerPixel)
		}
		updateScale()
		map?.on('move', updateScale)
		return () => {
			map?.off('move', updateScale)
		}
	}, [map, pref?.unitsDistance])

	const { distance, pixels } = getScaleWidth(pixelsPerDistance)

	return (
		<Box data-ta={ids.map.locationAndScaleBox.scale} flex col center width='100%'>
			{pref?.unitsDistance === 'KILOMETRES' /*|| pref?.unitsDistance === 'METRES'*/ && (
				<Typography variant='code2' sx={{ lineHeight: 1 }}>
					{distance < 1 ? `${distance * 1000} m` : `${distance} km`}
				</Typography>
			)}
			{pref?.unitsDistance === 'MILES' && (
				<Typography variant='code2' sx={{ lineHeight: 1 }}>
					{distance < 1 ? `${distance * 1760} yd` : `${distance} mi`}
				</Typography>
			)}
			{pref?.unitsDistance === 'NAUTICAL_MILES' && <Typography variant='code2' sx={{ lineHeight: 1 }}>{`${distance} nmi`}</Typography>}
			<Divider sx={{ borderColor: ({ palette }) => palette.text.secondary, borderWidth: 1.5 }} style={{ width: `${pixels}px` }} />
		</Box>
	)
}
